/**
 * Decoration — the layer that separates a marketplace-grade flyer from a
 * competent one.
 *
 * A Freepik or Envato sheet is dense: photographs sit on cards that carry a
 * shadow, a stamp is rotated over a corner, a texture block fills dead space,
 * brackets hold the trim, a ribbon crosses a photograph. None of that is
 * decoration for its own sake — each piece is doing a job (separating layers,
 * marking a claim, filling a void, framing the page), and the absence of all
 * of it is what made the first pass read as basic.
 *
 * Textures are single `svg` elements rather than hundreds of small shapes, so
 * a patterned block costs one layer in the editor and stays recolourable.
 */

import { SPACE, RULE, RADIUS, SHADOW } from '../system/tokens';
import { text, shape, image, svgIcon, page as _page } from '../system/elements';

/** L-shaped brackets at the four corners — holds the trim without a full frame. */
export const cornerBrackets = (idBase, { x, y, width, height, arm = 150, weight = RULE.thin, color }) => {
  const els = [];
  const corner = (k, cx, cy, dx, dy) => {
    els.push(shape(`${idBase}-bracket-${k}-h`, {
      x: dx < 0 ? cx - arm : cx, y: cy, width: arm, height: weight, fill: color,
    }));
    els.push(shape(`${idBase}-bracket-${k}-v`, {
      x: cx, y: dy < 0 ? cy - arm : cy, width: weight, height: arm, fill: color,
    }));
  };
  corner('tl', x, y, 1, 1);
  corner('tr', x + width - weight, y, -1, 1);
  corner('bl', x, y + height - weight, 1, -1);
  corner('br', x + width - weight, y + height - weight, -1, -1);
  return els;
};

/** A field of small dots — texture for space that would otherwise read as empty. */
export const dotGrid = (id, { x, y, width, height, color, step = 34, radius = 4 }) => {
  const cols = Math.max(1, Math.floor(width / step));
  const rows = Math.max(1, Math.floor(height / step));
  const circles = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      circles.push(`<circle cx="${c * step + step / 2}" cy="${r * step + step / 2}" r="${radius}" fill="{color}"/>`);
    }
  }
  const el = svgIcon(id, {
    x, y, width, height, fill: color,
    content: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cols * step} ${rows * step}">${circles.join('')}</svg>`,
  });
  el.meta = { texture: true };
  return el;
};

/** Diagonal hatching — a denser texture than dots, for narrow accent strips. */
export const hatch = (id, { x, y, width, height, color, step = 26, weight = 5 }) => {
  const span = width + height;
  const lines = [];
  for (let i = -height; i < span; i += step) {
    lines.push(`<line x1="${i}" y1="0" x2="${i + height}" y2="${height}" stroke="{color}" stroke-width="${weight}"/>`);
  }
  const el = svgIcon(id, {
    x, y, width, height, fill: color,
    content: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">${lines.join('')}</svg>`,
  });
  el.meta = { texture: true };
  return el;
};

/**
 * A card: shadowed plate with optional accent edge. Everything that should
 * read as sitting ABOVE the page goes on one of these.
 */
export const card = (idBase, { x, y, width, height, fill, radius = RADIUS.md, accentColor, accentWidth = RULE.thick, shadow = SHADOW.card }) => {
  const els = [shape(`${idBase}-card`, {
    x, y, width, height, fill, cornerRadius: radius,
  })];
  els[0].shadowOffsetX = shadow.offsetX;
  els[0].shadowOffsetY = shadow.offsetY;
  els[0].shadowBlur = shadow.blur;
  els[0].shadowColor = shadow.color;

  if (accentColor) {
    els.push(shape(`${idBase}-card-edge`, {
      x, y, width: accentWidth, height, fill: accentColor,
    }));
  }
  return els;
};

/**
 * Rotated stamp — a filled disc with a ring inside it and up to three centred
 * lines. This is the "JUST LISTED" roundel that marks the claim.
 */
export const stamp = (idBase, r, { x, y, size, lines, fill, inkColor, rotation = -12, ring = true }) => {
  const els = [shape(`${idBase}-stamp`, {
    x, y, width: size, height: size, kind: 'circle', fill, rotation,
  })];

  if (ring) {
    const inset = Math.round(size * 0.07);
    els.push(shape(`${idBase}-stamp-ring`, {
      x: x + inset, y: y + inset, width: size - inset * 2, height: size - inset * 2,
      kind: 'circle', fill: 'none', stroke: inkColor, strokeWidth: RULE.thin, rotation,
    }));
  }

  // Lines are laid on the stamp's own centre, then rotated with it.
  const total = lines.reduce((a, l) => a + Math.ceil(l.size * 1.32), 0);
  let ly = y + (size - total) / 2;
  lines.forEach((l, i) => {
    els.push(text(`${idBase}-stamp-${i}`, r.h3, l.copy, { x, y: ly, width: size, align: 'center' },
      { color: inkColor, fontSize: l.size, letterSpacing: l.tracking ?? 2, rotation }));
    ly += Math.ceil(l.size * 1.32);
  });
  return els;
};

/** Angled ribbon crossing a photograph, with a single line of copy on it. */
export const ribbon = (idBase, r, { x, y, width, height, copy, fill, inkColor, rotation = -6, fontSize = 44 }) => [
  shape(`${idBase}-ribbon`, { x, y, width, height, fill, rotation }),
  text(`${idBase}-ribbon-copy`, r.cta, copy, {
    x, y: y + Math.round((height - fontSize * 1.4) / 2), width, align: 'center',
  }, { color: inkColor, fontSize, rotation }),
];

/**
 * Layered photograph — a photo on a card, with a second photo overlapping its
 * corner. The overlap is what gives a flat sheet depth.
 */
export const layeredPhotos = (idBase, { x, y, width, height, main, inset, insetScale = 0.42, cardColor, accentColor, bound }) => {
  const els = [];
  const PAD = SPACE.xs;
  // `bound` is the furthest right the inset — and the card behind it — may
  // reach. Without it the inset is placed purely by overlap and runs off the
  // sheet: it was 7px past the trim on one template and 59px on another, which
  // reads as a printing mistake rather than a layered composition.
  const limit = bound ?? x + width;

  els.push(...card(`${idBase}-main`, {
    x: x - PAD, y: y - PAD, width: width + PAD * 2, height: height + PAD * 2,
    fill: cardColor, radius: RADIUS.sm,
  }));
  els.push(image(`${idBase}-photo-main`, { x, y, width, height, src: main, cornerRadius: RADIUS.sm }));

  const iw = Math.round(width * insetScale);
  const ih = Math.round(height * insetScale);
  const ix = Math.min(x + width - Math.round(iw * 0.62), limit - iw - PAD);
  const iy = y + height - Math.round(ih * 0.55);

  els.push(...card(`${idBase}-inset`, {
    x: ix - PAD, y: iy - PAD, width: iw + PAD * 2, height: ih + PAD * 2,
    fill: cardColor, radius: RADIUS.sm, shadow: SHADOW.lift,
  }));
  els.push(image(`${idBase}-photo-inset`, { x: ix, y: iy, width: iw, height: ih, src: inset, cornerRadius: RADIUS.sm }));

  if (accentColor) {
    els.push(shape(`${idBase}-inset-edge`, {
      x: ix - PAD, y: iy + ih + PAD - RULE.medium, width: iw + PAD * 2, height: RULE.medium, fill: accentColor,
    }));
  }
  return els;
};

/** Gradient wash over a photograph, from one edge. */
export const wash = (id, { x, y, width, height, color, angle = 180, from = 0, to = 0.95 }) =>
  shape(id, {
    x, y, width, height, fill: color,
    gradient: { type: 'linear', angle, colors: [color, color], opacities: [from, to], positions: [0, 100] },
  });

/** Icon inside a filled disc — the spec-row treatment that reads as designed. */
export const iconDisc = (idBase, { x, y, size, content, discFill, iconColor, ringColor }) => {
  const els = [shape(`${idBase}-disc`, { x, y, width: size, height: size, kind: 'circle', fill: discFill })];
  if (ringColor) {
    els.push(shape(`${idBase}-disc-ring`, {
      x: x - 6, y: y - 6, width: size + 12, height: size + 12, kind: 'circle',
      fill: 'none', stroke: ringColor, strokeWidth: RULE.hair,
    }));
  }
  const inner = Math.round(size * 0.52);
  els.push(svgIcon(`${idBase}-disc-icon`, {
    x: x + (size - inner) / 2, y: y + (size - inner) / 2, width: inner, height: inner,
    content, fill: iconColor,
  }));
  return els;
};
